import AsyncSelect from 'react-select/async';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { CustomReactSelectStyles } from 'pages/Promotions/styles/customStyles';
import {
  CustomerESSearchParams,
  CustomerSearchESFieldType,
  getSearchCustomersFromES,
} from 'services/customerService';
import { TFunction } from 'i18next';

interface OptionType {
  value: string;
  label: string;
}

const FIELD_INDEX: Record<CustomerSearchESFieldType, number> = {
  account_number: 0,
  name: 1,
  phone: 2,
  email: 3,
};

interface CustomerAutoCompleteProps {
  t: TFunction;
  id: string;
  name: string;
  value: string;
  onChange?: (option: OptionType | null) => void;
  onOptionSelect?: (option: OptionType | null, totalResultIds: string[]) => void;
  onInputValueChange?: (value: string) => void;
  onBlur: () => void;
  isDarkMode: boolean;
  searchType: CustomerSearchESFieldType;
  allValues: CustomerESSearchParams;
  placeholder?: string;
}

const CustomerAutoComplete = ({
  t,
  id,
  name,
  value,
  onChange,
  onOptionSelect,
  onInputValueChange,
  onBlur,
  isDarkMode,
  searchType,
  allValues,
  placeholder,
}: CustomerAutoCompleteProps) => {
  const latestRequestRef = useRef(0);
  const lastOptionsRef = useRef<OptionType[]>([]);
  const totalResultsRef = useRef<string[]>([]);
  const optionResultIdsRef = useRef<Map<string, string[]>>(new Map());
  const skipNextBlurRef = useRef(false);
  const latestSelectionRef = useRef(0);
  const [pendingSelectedOption, setPendingSelectedOption] =
    useState<OptionType | null>(null);
  const externalSelectedOption = useMemo(
    () => (value ? { value, label: value } : null),
    [value]
  );
  const selectedOptionValue = pendingSelectedOption ?? externalSelectedOption;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectRenderKey, setSelectRenderKey] = useState(0);

  // Use ref so debouncedFetcher closure always reads latest allValues
  // without needing allValues in useMemo deps (which would recreate/cancel the debounce)
  const allValuesRef = useRef(allValues);
  useEffect(() => {
    allValuesRef.current = allValues;
  }, [allValues]);

  const debouncedFetcher = useMemo(() => {
    const fn = debounce(
      async (keyword: string, callback: (options: OptionType[]) => void) => {
        const requestId = ++latestRequestRef.current;

        try {
          const params: CustomerESSearchParams = {
            ...allValuesRef.current,
            [searchType]: keyword,
          };

          const res = await getSearchCustomersFromES(params);

          if (requestId !== latestRequestRef.current) return;

          const totalResultIds = res.data?.total_results?.list ?? [];
          totalResultsRef.current = totalResultIds;

          const fieldIdx = FIELD_INDEX[searchType];
          const seen = new Set<string>();
          const optionIdsMap = new Map<string, string[]>();
          const apiOptions: OptionType[] = (res.data?.limit_results?.list ?? [])
            .map((item: string, index: number) => {
              const value = item.split('|')[fieldIdx] || '';
              const resultId = totalResultIds[index];

              if (value && resultId) {
                const ids = optionIdsMap.get(value) ?? [];
                ids.push(resultId);
                optionIdsMap.set(value, ids);
              }

              return value;
            })
            .filter((v: string) => {
              if (!v || seen.has(v)) return false;
              seen.add(v);
              return true;
            })
            .map((v: string) => ({ value: v, label: v }));

          optionIdsMap.set(keyword, totalResultIds);
          optionResultIdsRef.current = optionIdsMap;

          let finalOptions: OptionType[] = [];

          if (apiOptions.length === 0) {
            finalOptions = [{ value: keyword, label: keyword }];
          } else {
            const exactMatch = apiOptions.some(
              (opt) => opt.value.toLowerCase() === keyword.toLowerCase()
            );

            finalOptions = exactMatch
              ? apiOptions
              : [{ value: keyword, label: keyword }, ...apiOptions];
          }

          lastOptionsRef.current = finalOptions;
          callback(finalOptions);
        } catch (error) {
          console.error(error);

          const fallbackOption = [{ value: keyword, label: keyword }];
          lastOptionsRef.current = fallbackOption;
          callback(fallbackOption);
        }
      },
      400
    );

    return fn;
  }, [searchType]);

  useEffect(() => {
    return () => {
      debouncedFetcher.cancel();
    };
  }, [debouncedFetcher]);

  useEffect(() => {
    setPendingSelectedOption((current) => {
      if (!current) return current;
      if (!value || current.value === value) {
        return null;
      }
      return current;
    });
  }, [value]);

  // Stable reference so react-select/async does not reset the debounce on every re-render
  const loadOptions = useCallback(
    (keyword: string, callback: (options: OptionType[]) => void) => {
      if (!keyword) {
        callback(lastOptionsRef.current);
        return;
      }

      if (keyword.length < 3) {
        callback([]);
        return;
      }

      debouncedFetcher(keyword, callback);
    },
    [debouncedFetcher]
  );

  const resolveSelectedOptionIds = useCallback(
    async (option: OptionType | null) => {
      if (!option) {
        return totalResultsRef.current;
      }

      const optionResultIds = optionResultIdsRef.current.get(option.value);
      if (optionResultIds) {
        return optionResultIds;
      }

      try {
        const response = await getSearchCustomersFromES({
          ...allValuesRef.current,
          [searchType]: option.value,
        });

        return response.data?.total_results?.list ?? [];
      } catch (error) {
        console.error('Failed to resolve selected customer option:', error);
        return totalResultsRef.current;
      }
    },
    [searchType]
  );

  return (
    <AsyncSelect<OptionType, false>
      key={`${id}-${selectRenderKey}`}
      id={id}
      name={name}
      value={selectedOptionValue}
      onInputChange={(newValue, { action }) => {
        if (action === 'input-change') {
          latestSelectionRef.current += 1;
          skipNextBlurRef.current = false;
          setPendingSelectedOption(null);
          onInputValueChange?.(newValue);
        }
      }}
      onChange={async (option) => {
        const selectionId = ++latestSelectionRef.current;

        skipNextBlurRef.current = true;
        setPendingSelectedOption(option);
        setIsMenuOpen(false);
        setSelectRenderKey((current) => current + 1);

        if (onOptionSelect) {
          const resolvedIds = await resolveSelectedOptionIds(option);
          if (selectionId !== latestSelectionRef.current) return;
          onOptionSelect(option, resolvedIds);
        } else {
          if (selectionId !== latestSelectionRef.current) return;
          onChange?.(option);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !isMenuOpen) {
          event.preventDefault();
        }
      }}
      onMenuOpen={() => {
        setIsMenuOpen(true);
      }}
      onMenuClose={() => {
        setIsMenuOpen(false);
      }}
      onBlur={() => {
        setIsMenuOpen(false);
        window.setTimeout(() => {
          if (skipNextBlurRef.current) {
            skipNextBlurRef.current = false;
            return;
          }
          onBlur();
        }, 0);
      }}
      cacheOptions
      defaultOptions={lastOptionsRef.current}
      loadOptions={loadOptions}
      placeholder={placeholder}
      isClearable
      openMenuOnFocus
      styles={CustomReactSelectStyles(isDarkMode)}
      noOptionsMessage={() => t('No options')}
    />
  );
};

export default CustomerAutoComplete;
